/*
 * Copyright ©2020. Open Digital Solutions, Novi Sad. Sva prava zadržana.
 * Pravo da se koristi, kopira, modifikuje i distribuira ovaj softver i njegova dokumentacija
 * u bilo koje svrhe, bez naknade ili bez potpisanog sporazuma sa vlasnikom softvera, nije dozvoljeno.
 */

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { LocalStorageService } from 'src/app/local-storage.service';
import { Page } from 'src/app/page';
import { Pageable } from 'src/app/pageable';
import { environment } from 'src/environments/environment';
import { AppService } from './app.service';
import { ToastrImplService } from './toastr.service';
import printJS from 'print-js';
import { ErrorType, manageError } from 'src/app/utils/warningMessage';
import { ExpeditionService } from './expedition.service';

@Injectable({
   providedIn: 'root',
})
export class CpsuCasesService {
   constructor(private httpClient: HttpClient, private app: AppService, private localStorage: LocalStorageService, private toastr: ToastrImplService) {}

   getFilteredCasesById(pageable: Pageable, id: any, filter: any): Observable<Page<any>> {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });
      return this.httpClient.post(this.app.getCpsuUrl() + environment.getFilteredCpsuCasesByUserId + id + '/' + pageable.pageNumber + '/' + pageable.pageSize, filter, {
         headers,
      }) as Observable<any>;
   }

   getCases(primaryRole) {
      const headers = new HttpHeaders({
         'Content-type': 'application/x-www-form-urlencoded; charset=utf-8',
      });
      return this.httpClient.get(this.app.getCpsuUrl() + environment.getCpsuCasesUrl + '?primaryRole=' + primaryRole, { headers }) as Observable<any>;
   }

   getSocialCase(socialCaseId: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });
      return this.httpClient.get(this.app.getCpsuUrl() + environment.getCpsuCaseByIdUrl + '/' + socialCaseId, { headers }) as Observable<any>;
   }

   addConclusionFinishCase(fileToUpload: File, caseId: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });
      const formData: FormData = new FormData();
      formData.append('file', fileToUpload, fileToUpload.name);
      return this.httpClient.post(this.app.getCpsuUrl() + environment.addCpsuConclusionFinishCase + caseId, formData, {
         responseType: 'text',
      }) as Observable<any>;
   }

   createPlanNonSynthetizedReport(dto: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });

      return this.httpClient.post(this.app.getCpsuUrl() + environment.createNonSynthetizedPlan, dto, { headers }) as Observable<any>;
   }

   savePlanNonSynthetizedReport(dto: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });

      return this.httpClient.post(this.app.getCpsuUrl() + environment.saveNonSynthetizedPlan, dto, { headers }) as Observable<any>;
   }

   previewPlanNonSynthetizedReport(dto: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });
      this.httpClient.post(this.app.getCpsuUrl() + environment.previewNonSynthetizedPlan, dto, { responseType: 'blob', headers }).subscribe(data => {
         const file = new Blob([data], { type: 'application/pdf' });
         const fileURL = URL.createObjectURL(file);
         printJS(fileURL);
      });
   }

   findNonSynthetizedPlanByCaseId(caseId: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });

      return this.httpClient.get(this.app.getCpsuUrl() + environment.findNonSynthetizedPlanForCase + caseId, { headers }) as Observable<any>;
   }
   addConclusionFinishCaseWebDav(fileName: string, caseId: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });
      const params = new HttpParams().set('fileName', fileName);
      return this.httpClient.post(
         this.app.getCpsuUrl() + environment.addConclusionFinishCpsuCaseWebDav + caseId,
         {},
         {
            responseType: 'text',
            params,
         }
      ) as Observable<any>;
   }

   finishCase(
      msWordUsed: boolean,
      file: any,
      fileName: any,
      subjectId: any,
      caseId: any,
      caseName: any,
      expedition: ExpeditionService,
      dataSharingService: any,
      onErrorCallback: () => void,
   ) {
      if (msWordUsed) {
         this.addConclusionFinishCaseWebDav(fileName, caseId).subscribe(
            (documentId: string) => {
               this.localStorage.set('case', true);
               dataSharingService.cases.next(true);
               this.toastr.success('SNACKBAR.SOLUTION_CREATED');
               expedition.send({ documentId, caseId: caseId, caseName: caseName, subjectId: subjectId }, 'cpsu');
            },
            _ => {
               manageError(this.toastr, ErrorType.GENERATE_SOLUTION);
               onErrorCallback();
            }
         );
      } else {
         this.addConclusionFinishCase(file, caseId).subscribe(
            (documentId: string) => {
               this.localStorage.set('case', true);
               dataSharingService.cases.next(true);
               this.toastr.success('SNACKBAR.SOLUTION_CREATED');
               expedition.send({ documentId, caseId: caseId, caseName: caseName, subjectId: subjectId }, 'cpsu');
            },
            _ => {
               manageError(this.toastr, ErrorType.GENERATE_SOLUTION);
               onErrorCallback();
            }
         );
      }
   }

   checkActiveRightsForArchive(cpsuSubjectId: any) {
      return this.httpClient.get(this.app.getCpsuUrl() + environment.checkActiveCpsuCasesForArchive + cpsuSubjectId, { responseType: 'text' }) as Observable<any>;
   }

   sendIntoPlanner(caseId: any, plannerReason: any) {
      const headers = new HttpHeaders({
         'Content-type': 'application/json',
      });
      return this.httpClient.post(this.app.getCpsuUrl() + environment.sendCpsuCaseIntoPlanner + caseId, plannerReason, { headers }) as Observable<any>;
   }
}
